import { Button, Col, Form, Input, Row, Select, Spin, Switch, TimePicker, Upload, message } from 'antd';
import React, { useEffect, useState } from 'react';

import { UploadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { RIDER_TYPE, TIME_FORMAT, TYPE_PAGE, VEHICLE_TYPE } from '../../../constants';
import { selectAllVehicles } from '../../../modules/vehicles/stores/slice';
import { doGetVehicles } from '../../../modules/vehicles/stores/thunk';
import { getVehicleEmoji } from '../Vehicles/VehicleForm';

const initValuesForm = {
  region: 'Amsterdam',
  riderType: RIDER_TYPE.FIXED,
  vehicleType: VEHICLE_TYPE[0],
  isActive: true,
  email: '',
  password: '',
};

const RiderForm = ({ handleCancel, handleOk, rider, isModalOpen, isFetching, typePage = TYPE_PAGE.NEW }) => {
  const dispatch = useDispatch();

  const [formRider] = Form.useForm();
  const [validTimeRange, setValidTimeRange] = useState(true);
  const [riderType, setRiderType] = useState('');
  const [fileList, setFileList] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const isFetchingVehicle = useSelector((state) => state.vehicle.isFetching);
  const vehicles = useSelector((state) => selectAllVehicles(state));

  useEffect(() => {
    dispatch(doGetVehicles());
  }, []);

  useEffect(() => {
    if (rider) {
      formRider.setFieldsValue({ ...rider });
      setRiderType(rider.riderType);
      if (rider.avatar) {
        setFileList([
          {
            uid: rider.id + 'avatar',
            status: 'done',
            url: rider.avatarUrl,
            name: rider.avatar,
          },
        ]);
      }
    } else {
      formRider.resetFields();
      setFileList([]);
    }
  }, [formRider, isModalOpen, rider]);

  const onFinishForm = (values) => {
    const params = {
      name: values.name,
      riderType: values.riderType,
      email: values.email,
      merchantId: values.merchantId,
      avatar: values.avatar,
      phone: values.phone,
      vehicleId: values.vehicleId,
      regions: values.regions,
      timeStart: values.startTime ? values.startTime.format(TIME_FORMAT) : undefined,
      timeEnd: values.endTime ? values.endTime.format(TIME_FORMAT) : undefined,
      isActive: values.isActive,
    };
    if (values.password) params.password = values.password;
    handleOk && handleOk(params);
  };

  const onCancel = () => {
    handleCancel && handleCancel();
  };

  const onOk = () => {
    formRider.submit();
  };

  const handleChange = (info) => {
    let newFileList = [...info.fileList];
    const file = newFileList.slice(-1)[0];
    const isLt5M = file?.size ? file?.size / 1024 / 1024 <= 5 : true;
    if (!isLt5M) {
      messageApi.open({
        type: 'error',
        content: 'Image must smaller than 5MB!',
      });
    } else {
      newFileList = newFileList.slice(-1);
      setFileList(newFileList);
    }
  };

  const handleTypeChange = () => {
    const type = formRider.getFieldValue('riderType');
    setRiderType(type);
  };

  const handleTimeRangeChange = () => {
    const startTime = formRider.getFieldValue('startTime');
    const endTime = formRider.getFieldValue('endTime');

    if (startTime && endTime) {
      if (startTime.isBefore(endTime)) {
        setValidTimeRange(true);
      } else {
        setValidTimeRange(false);
      }
    } else if (!endTime) {
      setValidTimeRange(true);
    }
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  return (
    <Spin spinning={isFetching || isFetchingVehicle}>
      {contextHolder}
      <div className="RiderForm">
        <Form
          form={formRider}
          scrollToFirstError
          layout="vertical"
          requiredMark={false}
          colon={false}
          className="FormContent"
          onFinish={onFinishForm}
          initialValues={initValuesForm}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Rider Name" name="name" rules={[{ required: true, message: 'Required' }]}>
                <Input size="large" placeholder="Rider Name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Rider Type" name="riderType" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  options={Object.keys(RIDER_TYPE).map((key) => ({ value: RIDER_TYPE[key], label: key }))}
                  onChange={handleTypeChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div className="switch-active">
                <Form.Item name="isActive" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <div className="text">
                  <p>Active</p>
                </div>
              </div>
            </Col>
            {riderType == 'FREELANCE' && (
              <Col span={24}>
                <Form.Item label="Merchant ID" name="merchantId">
                  <Input size="large" placeholder="Merchant ID" initialValues="xxx" />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ required: true, type: 'email', message: 'The input is not valid E-mail!' }]}
              >
                <Input
                  disabled={typePage === TYPE_PAGE.EDIT}
                  size="large"
                  placeholder="Email"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: typePage === TYPE_PAGE.NEW, message: 'Required' }]}
              >
                <Input.Password size="large" placeholder="Password" autoComplete="new-password" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Avatar" name="avatar" getValueFromEvent={normFile}>
                <Upload.Dragger
                  multiple={false}
                  maxCount={1}
                  accept=".png, .jpg, .jpeg"
                  showUploadList={{
                    showPreviewIcon: false,
                    showDownloadIcon: false,
                    showRemoveIcon: true,
                  }}
                  customRequest={async ({ onSuccess, onError }) => {
                    try {
                      onSuccess('ok');
                    } catch (e) {
                      console.log(e);
                      onError();
                    }
                  }}
                  onRemove={() => setFileList([])}
                  listType="picture-card"
                  fileList={fileList}
                  onChange={handleChange}
                >
                  <p className="upload-text">Drag and drop to upload or</p>
                  <p className="upload-drag-icon">
                    Browse
                    <UploadOutlined />
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Phone Number"
                name="phone"
                rules={[
                  { required: true, message: 'Required' },
                  {
                    pattern: /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
                    message: 'Please enter a valid phone',
                  },
                ]}
              >
                <Input size="large" placeholder="Phone Number" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Vehicle" name="vehicleId" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  options={vehicles.map((vehicle) => ({
                    value: vehicle.id,
                    label: getVehicleEmoji(vehicle?.vehicleCategory) + vehicle.name || vehicle.vehicleId,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label="Start Time"
                rules={[
                  {
                    required: true,
                    message: 'Required',
                  },
                ]}
              >
                <TimePicker
                  format={TIME_FORMAT}
                  size="large"
                  placeholder="Start Time"
                  onChange={handleTimeRangeChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label="End Time"
                rules={[
                  {
                    required: true,
                    message: 'Required',
                  },
                ]}
                validateStatus={!validTimeRange ? 'error' : undefined}
                help={!validTimeRange ? 'End time must be after start time' : undefined}
              >
                <TimePicker format={TIME_FORMAT} size="large" placeholder="End Time" onChange={handleTimeRangeChange} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <div className="FormFooter">
          <Button className="ButtonCancel" onClick={onCancel}>
            Cancel
          </Button>
          <Button className="CreateButton ButtonPrimary" type="primary" onClick={onOk}>
            {rider ? 'Edit' : 'Create'}
            <ReactSVG className="icon" src="/icons/done.svg" />
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default RiderForm;
